import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Send, CheckCircle, Loader2, CalendarDays, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Full Respray",
  "Custom Paint Job",
  "Colour Matching",
  "Dent & Scratch Repair",
  "Clear Coat Restoration",
  "Ceramic Coating",
  "Detailing",
  "Other",
];

const TIME_SLOTS = [
  "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM",
  "4:00 PM", "4:30 PM",
  "5:00 PM",
];

export default function BookingPage() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", vehicle_model: "", service_type: "", description: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-AU", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.vehicle_model || !formData.service_type || !selectedDate || !selectedTime) {
      toast.error("Please fill in all required fields, including date and time.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        preferred_date: formatDate(selectedDate),
        preferred_time: selectedTime,
      });
      setSubmitted(true);
      toast.success("Appointment booked! We will confirm shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try texting us directly.");
    } finally {
      setLoading(false);
    }
  };

  const disabledDays = { before: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) }; // Minimum 2 days out

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] pt-28 pb-20 px-6 md:px-12">
        <div className="max-w-[600px] mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#09090b] border border-white/5 p-16 text-center">
            <CheckCircle size={48} className="text-[#3b82f6] mx-auto mb-6" />
            <h3 data-testid="booking-success-heading" className="font-heading text-3xl sm:text-4xl uppercase tracking-wide text-white mb-4">
              Appointment Requested
            </h3>
            <p className="text-zinc-400 font-body text-base max-w-md mx-auto mb-3">
              {formatDate(selectedDate)} at {selectedTime}
            </p>
            <p className="text-zinc-500 font-body text-sm max-w-md mx-auto mb-8">
              We will confirm your booking shortly. If you need to make any changes, give us a call.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate("/")} data-testid="booking-back-home"
                className="border border-white/20 text-white font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-white/5 transition-colors cursor-pointer bg-transparent">
                Back to Home
              </button>
              <a href="sms:0450645926" data-testid="booking-call"
                className="border border-white/20 text-white font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-white/5 transition-colors no-underline text-center">
                Text 0450 645 926
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-20" data-testid="booking-page">
      {/* Back navigation */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12">
        <button onClick={() => navigate("/")} data-testid="booking-back-btn"
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm tracking-[0.15em] uppercase font-body transition-colors bg-transparent border-none cursor-pointer">
          <ArrowLeft size={16} />
          Back to Home
        </button>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Schedule</span>
          </div>
          <h1 data-testid="booking-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95]">
            Book an Appointment
          </h1>
          <p className="text-zinc-500 font-body text-base mt-4">Open 7 days a week, 9am to 5pm. Appointment bookings available from 11am onwards.</p>
          <p className="text-zinc-600 font-body text-sm mt-2">
            <span className="text-[#3b82f6]">Note:</span> Online bookings require a minimum of 2 days notice. Need something sooner? Just text us and we will sort you out.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} data-testid="booking-form">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-[1px] bg-white/5">
            {/* Column 1: Calendar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-[#09090b] p-8 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <CalendarDays size={18} className="text-[#3b82f6]" />
                <h3 className="font-heading text-xl uppercase tracking-wide text-white">Pick a Date *</h3>
              </div>
              <div data-testid="booking-calendar" className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  className="bg-[#09090b] text-white"
                />
              </div>
              {selectedDate && (
                <p className="mt-4 text-sm font-mono text-[#3b82f6] text-center" data-testid="booking-selected-date">
                  {formatDate(selectedDate)}
                </p>
              )}
            </motion.div>

            {/* Column 2: Time */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-[#09090b] p-8 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <Clock size={18} className="text-[#3b82f6]" />
                <h3 className="font-heading text-xl uppercase tracking-wide text-white">Pick a Time *</h3>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="booking-time-slots">
                {TIME_SLOTS.map((time) => (
                  <button type="button" key={time} onClick={() => setSelectedTime(time)}
                    data-testid={`booking-time-${time.replace(/[\s:]/g, "-").toLowerCase()}`}
                    className={`px-3 py-3 text-sm font-mono border cursor-pointer transition-colors ${
                      selectedTime === time
                        ? "bg-white text-black border-white"
                        : "bg-transparent text-zinc-500 border-white/10 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Column 3: Details */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="bg-[#09090b] p-8 md:p-10"
            >
              <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-6">Your Details</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-600 font-body mb-2">Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" data-testid="booking-input-name"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-3 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" required />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-600 font-body mb-2">Phone *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="04XX XXX XXX" data-testid="booking-input-phone"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-3 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" required />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-600 font-body mb-2">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" data-testid="booking-input-email"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-3 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-600 font-body mb-2">Vehicle *</label>
                  <input type="text" name="vehicle_model" value={formData.vehicle_model} onChange={handleChange} placeholder="e.g. Nissan Silvia S15" data-testid="booking-input-vehicle"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-3 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" required />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-600 font-body mb-3">Service *</label>
                  <div className="flex flex-wrap gap-2" data-testid="booking-service-group">
                    {SERVICE_OPTIONS.map((s) => (
                      <button type="button" key={s} onClick={() => setFormData(p => ({...p, service_type: s}))}
                        data-testid={`booking-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-3 py-1.5 text-xs tracking-[0.05em] uppercase font-body border cursor-pointer transition-colors ${
                          formData.service_type === s ? "bg-white text-black border-white" : "bg-transparent text-zinc-500 border-white/10 hover:border-white/30 hover:text-white"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-600 font-body mb-2">Notes</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows={2} placeholder="Any extra details..."
                    data-testid="booking-input-description"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-3 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm resize-none" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Submit */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            {selectedDate && selectedTime && (
              <p className="text-zinc-400 font-body text-sm">
                Booking for <span className="text-white font-mono">{formatDate(selectedDate)}</span> at <span className="text-white font-mono">{selectedTime}</span>
              </p>
            )}
            <button type="submit" disabled={loading} data-testid="booking-submit-btn"
              className="flex items-center justify-center gap-3 bg-white text-black font-heading text-lg tracking-[0.15em] uppercase px-12 py-4 hover:bg-zinc-200 transition-colors cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed ml-auto">
              {loading ? (<><Loader2 size={18} className="animate-spin" />Booking...</>) : (<>Book Appointment<Send size={16} /></>)}
            </button>
          </motion.div>
        </form>
      </div>
    </div>
  );
}
